import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { NOTIFICATION_TYPES, setMessage } from '../../../toasts';
import { handleAxiosErrorMessage } from '../../../../utils';
import { useGetLeadEvents, useLeadEventsCache } from '../../accountCreation';
import { clearTrainingProgress } from '../../training';
import { useOnboardingToken } from '../../shared';
import { completedSectionIdsFromEvents } from '../utils/registrationLeadEvents';
import { REGISTRATION_SECTION_KEYS } from '../utils/registration.constants';
import { useRequestExternalValidation } from './useRequestExternalValidation';

const REGISTRATION_SECTIONS = [
  { id: REGISTRATION_SECTION_KEYS.PERSONAL_INFO },
  { id: REGISTRATION_SECTION_KEYS.VEHICLE_INFO },
  { id: REGISTRATION_SECTION_KEYS.DRIVERS_LICENSE },
];
const EXTERNAL_VALIDATION_INVALID_RESPONSE =
  'EXTERNAL_VALIDATION_INVALID_RESPONSE';

export function useRegistrationMenu() {
  const { t } = useTranslation(['onboarding', 'system']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { refetchLeadEvents } = useLeadEventsCache();
  const dismissSuccessModalLock = useRef(false);
  const { token } = useOnboardingToken();

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    clearTrainingProgress();
  }, []);

  const { data: leadEvents = [] } = useGetLeadEvents({
    enabled: Boolean(token),
  });

  const completedSections = completedSectionIdsFromEvents(leadEvents);

  const allCompleted =
    completedSections.length === REGISTRATION_SECTIONS.length;

  const requestExternalValidationMutation = useRequestExternalValidation({
    invalidateEventsOnSuccess: false,
    onSuccess: () => {
      setShowSuccessModal(true);
    },
    onError: (error) => {
      const code = handleAxiosErrorMessage(error);
      if (code === EXTERNAL_VALIDATION_INVALID_RESPONSE) {
        dispatch(
          setMessage({
            body: t(
              'registration.menu.errors.external_validation_invalid_response'
            ),
            type: NOTIFICATION_TYPES.ERROR,
          })
        );
        return;
      }
      const systemText = t(code, { ns: 'system', defaultValue: '' });
      const hasSystemTranslation = systemText && systemText !== code;
      dispatch(
        setMessage({
          body: hasSystemTranslation
            ? `${systemText} ${t('registration.menu.errors.retry_hint')}`
            : t('registration.menu.errors.external_validation_failed'),
          type: NOTIFICATION_TYPES.ERROR,
        })
      );
    },
  });

  const getSectionLabel = (sectionId) =>
    t(`registration.menu.sections.${sectionId}`);

  const menuItems = REGISTRATION_SECTIONS.map((section) => {
    const completed = completedSections.includes(section.id);
    return {
      id: section.id,
      label: getSectionLabel(section.id),
      completed,
      interactive: !completed,
    };
  });

  const handleSubmit = async () => {
    try {
      await requestExternalValidationMutation.mutateAsync();
    } catch {
      /* empty */
    }
  };

  const handleDismissSuccessModal = async () => {
    if (dismissSuccessModalLock.current) return;
    dismissSuccessModalLock.current = true;
    setShowSuccessModal(false);
    await refetchLeadEvents();
    navigate('/onboarding/pending');
  };

  return {
    sections: REGISTRATION_SECTIONS,
    completedSections,
    menuItems,
    allCompleted,
    isSubmittingRegistration: requestExternalValidationMutation.isPending,
    isRegistrationSubmitSuccess: requestExternalValidationMutation.isSuccess,
    showSuccessModal,
    handleSubmit,
    handleDismissSuccessModal,
  };
}
